import React, { useState, useEffect, useRef, useMemo } from 'react';
import Plot from 'react-plotly.js';
import MentionAutocomplete, { buildRefLabel } from '../genbi/MentionAutocomplete';

const GraphComponent = ({ graphData, isLoading, contentId, subtitle, footer, isEditing = false, editedTitle, onFieldChange, isInPlace = false, hideContentLabel = false,
  docs = [], refs = [], onNeedReferences, onCreateReference, refsLoading = false }) => {
  const plotRef = useRef(null);
  const subtitleRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );

  const processDocTags = (text) => {
    if (!text) return text;
    const maxLen = 15;
    // A raw token comes from an unsaved edit (Save Changes); build the same pill the
    // backend builds on load, with the document / reference name.
    const chip = (cls, token, label) =>
      `<span class="${cls} m-0 mx-1 ev-btn-xsmall ev-bold-reg" data-ref="${token}">${label}</span>`;
    let processed = text
      .replace(/(<[^>]*>)|\[doc_id_(\d+)\]/g, (match, tag, id) => {
        if (tag) return tag;
        const doc = docs.find(d => Number(d.id) === Number(id));
        return chip('ev-message-card6', match, doc?.name || `Document ${id}`);
      })
      .replace(/(<[^>]*>)|\[ar_id_(\d+)\]/g, (match, tag, id) => {
        if (tag) return tag;
        const ref = refs.find(r => Number(r.ar_id) === Number(id));
        return chip('ev-message-card7', match, ref ? buildRefLabel(ref) : `Reference ${id}`);
      });
    processed = processed.replace(
      /<span([^>]*\b(?:ev-message-card6|ev-message-card7)\b[^>]*)>([\s\S]*?)<\/span>/g,
      (full, attrs, inner) => {
        const rawText = (inner || '').trim();
        if (rawText.includes('<')) return full;
        const shortText = rawText.length <= maxLen ? rawText : rawText.slice(0, maxLen - 1) + '…';
        const hasTitle = /\stitle=/.test(attrs) || /\sdata-src=/.test(attrs);
        const safeTitle = rawText.replace(/"/g, '&quot;');
        const newAttrs = hasTitle ? attrs : `${attrs} title="${safeTitle}"`;
        return `<span${newAttrs}>${shortText}</span>`;
      }
    );
    processed = processed.replace(/\bclassName=/g, 'class=');
    return processed;
  };

  const processedSubtitle = useMemo(() => processDocTags(subtitle), [subtitle, docs, refs]);
  const processedFooter = useMemo(() => processDocTags(footer), [footer, docs, refs]);
  
  const getCSSVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  };

  // Get font size from CSS class
  const getFontSize = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontSize = parseFloat(getComputedStyle(tempElement).fontSize);
    document.body.removeChild(tempElement);
    return fontSize;
  };

  const getMargin = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const margin = parseFloat(getComputedStyle(tempElement).marginLeft);
    document.body.removeChild(tempElement);
    return margin;
  };
  
  // Get font weight from CSS class
  const getFontWeight = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontWeight = getComputedStyle(tempElement).fontWeight;
    document.body.removeChild(tempElement);
    return fontWeight;
  };

  // Get font family from CSS class
  const getFontFamily = (className) => {
    const tempElement = document.createElement('span');
    tempElement.className = className;
    tempElement.style.visibility = 'hidden';
    document.body.appendChild(tempElement);
    const fontFamily = getComputedStyle(tempElement).fontFamily;
    document.body.removeChild(tempElement);
    return fontFamily;
  };

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme');
          setIsDarkMode(newTheme === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (plotRef.current && plotRef.current.el) {
        try {
          window.Plotly?.purge(plotRef.current.el);
        } catch (error) {
          console.debug('Plotly cleanup handled:', error.message);
        }
      }
    };
  }, []);


  useEffect(() => {
    const styleId = 'plotly-modebar-styles';
    
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .modebar-btn {
          width: var(--px-30) !important;
          height: var(--px-30) !important; 
          padding: var(--px-5) !important;
        }
        .modebar-btn svg {
          width: var(--px-16) !important;
          height: var(--px-16) !important;
        }
        /* Modebar button tooltip text styling */
        [data-title]:before,
        [data-title]:after {
          font-size: var(--px-11) !important;
          line-height: 1.4 !important;
        }
        /* Hover tooltip styling */
        .hoverlayer .hovertext path {
          stroke: var(--color8) !important;
        }
        .hoverlayer .hovertext text {
          font-size: var(--px-11) !important;
          font-family: var(--poppinsfont) !important;
        }
        g.hovertext text {
          font-size: var(--px-11) !important;
          font-family: var(--poppinsfont) !important;
        }
      `;
      document.head.appendChild(style);
    }
    
    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="graph-container align-items-center justify-content-center d-flex ">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <br />
          <span className="ev-reg-p-dark">
            Generating Plot
          </span>
        </div>
      </div>
    );
  }

  if (!graphData || !graphData.figure_dict) {
    return (
      <div className="container-error">
        <div className="text-center">
          <p>Error loading graph</p>
        </div>
      </div>
    );
  }

  // ═══ Deep-clone figure_dict to get plain mutable objects ═══
  // Redux Toolkit (Immer) freezes stored objects. Plotly.js internally
  // mutates data/layout passed to <Plot />.  Frozen proxies → silent render failure.
  // JSON round-trip produces a plain mutable copy that Plotly can work with.
  const figureDict = JSON.parse(JSON.stringify(graphData.figure_dict));

  // Calculate theme colors
  const themeColors = {
    paper_bgcolor: getCSSVariable('--background2'),
    plot_bgcolor: getCSSVariable('--background2'),
    fontColor: getCSSVariable('--color4'),  
    gridColor: getCSSVariable('--color12'),
    titleColor: getCSSVariable('--color2'),
  };

  // Get font sizes from CSS classes 
  const fontSize = getFontSize('ev-fs-11');
  const axisSize = getFontSize('ev-fs-11');
  const mainTitleSize = getFontSize('ev-fs-13');

  // Get font weights from CSS classes
  const mainTitleWeight = getFontWeight('ev-bold-med');
  const axisTitleWeight = getFontWeight('ev-bold-med');

  const marginLeft = getMargin('margin-left-60');
  const marginRight = getMargin('margin-right-60');
  const marginBottom = getMargin('margin-bottom-100');

  // Get font family from CSS class
  const fontFamily = getFontFamily('poppinsfont');

  // Detect if this is a network/state transition diagram
  const isNetworkDiagram = figureDict.layout?.meta?.chart_type === 'network_diagram';

  const layoutTitle = figureDict.layout?.title;
  const baseTitleConfig = typeof layoutTitle === 'object' && layoutTitle !== null ? layoutTitle : {};
  const currentTitleText = graphData?.title ?? (typeof layoutTitle === 'string' ? layoutTitle : layoutTitle?.text);
  const plotTitleText = editedTitle !== undefined ? editedTitle : currentTitleText;
  const plotTitleConfig = {
    ...baseTitleConfig,
    ...(plotTitleText !== undefined && { text: plotTitleText }),
  };

  // Dynamic axis configuration function
  const getAxisConfig = (axisName) => {
    const axisData = figureDict.layout?.[axisName];
    if (!axisData) return undefined;

    const isYAxis = axisName.startsWith('y');
    
    return {
      ...axisData,
      gridcolor: themeColors.gridColor,
      linecolor: themeColors.fontColor,
      tickcolor: themeColors.fontColor,
      automargin: true,
      ...(isYAxis && { fixedrange: false }),
      ...(!isYAxis && !isNetworkDiagram && { tickangle: -45 }),
      title: {
        ...axisData?.title,
        font: {
          ...axisData?.title?.font,
          family: fontFamily,
          color: themeColors.fontColor,
          size: axisSize,
          weight: axisTitleWeight
        }
      },
      tickfont: { 
        ...axisData?.tickfont,
        family: fontFamily,
        color: themeColors.fontColor,
        size: fontSize
      }
    };
  };

  // Generate all axes dynamically
  const dynamicAxes = {};
  Object.keys(figureDict.layout || {}).forEach(key => {
    if (key.match(/^[xy]axis\d*$/)) {  // Matches xaxis, yaxis, xaxis2, yaxis2, etc.
      const axisConfig = getAxisConfig(key);
      if (axisConfig) {
        dynamicAxes[key] = axisConfig;
      }
    }
  });

  // Dynamic annotations (subplot titles) configuration
  const getAnnotationConfig = () => {
    const annotations = figureDict.layout?.annotations;
    if (!annotations || annotations.length === 0) return undefined;

    return annotations.map(annotation => ({
      ...annotation,
      font: {
        ...annotation.font,
        family: fontFamily,
        color: themeColors.fontColor,
        size: fontSize,
        weight: axisTitleWeight
      },
      // Ensure annotations stay within bounds
      yanchor: annotation.yanchor || 'bottom',
      xanchor: annotation.xanchor || 'center'
    }));
  };

  const dynamicAnnotations = getAnnotationConfig();

  
  if (isInPlace) {
    return (
      <div className="genbi-inplace-figure">
        {subtitle && !hideContentLabel && (
          <div className="genbi-inplace-caption ev-fs-11 text-center mt-1 mb-1">
            <b>Figure {contentId}.</b> <span dangerouslySetInnerHTML={{ __html: processedSubtitle }} />
          </div>
        )}
        <Plot
          data={isNetworkDiagram
            ? figureDict.data.map(trace => ({
                ...trace,
                ...(trace.mode?.includes('text') && { textfont: { ...trace.textfont, color: themeColors.fontColor } }),
                ...(trace.mode === 'lines'        && { line:    { ...trace.line,    color: isDarkMode ? '#48CAE4' : '#0077B6' } }),
                ...(trace.marker && { marker: { ...trace.marker, color: isDarkMode ? '#0096C7' : '#0077B6', line: { ...trace.marker?.line, color: isDarkMode ? '#48CAE4' : '#023E8A' } } }),
              }))
            : figureDict.data
          }
          ref={plotRef}
          layout={{
            ...figureDict.layout,
            paper_bgcolor: themeColors.paper_bgcolor,
            plot_bgcolor:  themeColors.plot_bgcolor,
            font: { family: fontFamily, color: themeColors.fontColor, size: fontSize },
            autosize: true,
            margin: isNetworkDiagram ? { l: 10, r: 10, t: 10, b: 10 } : { l: marginLeft, r: 10, t: 30, b: marginBottom },
            legend: {
              ...figureDict.layout?.legend,
              bgcolor: themeColors.paper_bgcolor,
              bordercolor: themeColors.gridColor,
              font: { family: fontFamily, color: themeColors.fontColor, size: fontSize },
              orientation: 'v',
              x: 1, xanchor: 'right',
              y: 1, yanchor: 'top',
            },
            ...(isNetworkDiagram ? {
              xaxis: { visible: false, showgrid: false, zeroline: false, showline: false, showticklabels: false },
              yaxis: { visible: false, showgrid: false, zeroline: false, showline: false, showticklabels: false },
            } : dynamicAxes),
          }}
          config={{
            responsive: true,
            displayModeBar: true,
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
            doubleClick: 'reset',
            showTips: false,
          }}
          className='genbi-inplace-plot'
          useResizeHandler={false}
          key={isDarkMode ? 'dark-inplace' : 'light-inplace'}
        />
        {footer && (
          <div className="genbi-inplace-footer ev-fs-11 ev-small-p-dark mt-1">
            <i className="bi bi-pencil-square me-1"></i>
            <b>Note</b> <span dangerouslySetInnerHTML={{ __html: processedFooter }} />
          </div>
        )}
      </div>
    );
  }

  // ── Standalone rendering (default) ────────────────────────────────────────
  return (
    <div className="graph-container">
      {(isEditing || (subtitle && !hideContentLabel)) && (
        <div className="mb-2">
          {isEditing ? (
            <div className="genbi-figure-edit-field">
              <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
                <i className="bi bi-bar-chart-fill"></i> <b>Figure {contentId} — Subtitle</b>
              </span>
              <textarea
                ref={subtitleRef}
                className="formField2 ev-fs-11 w-100 genbi-footer-textarea"
                value={subtitle || ''}
                onChange={(e) => onFieldChange?.('subtitle', e.target.value)}
                rows={2}
                placeholder="Edit subtitle..."
              />
            </div>
          ) : (
            <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
              <i className="bi bi-bar-chart-fill"></i> <b>Figure {contentId}. </b> <span dangerouslySetInnerHTML={{ __html: processedSubtitle }} />
            </span>
          )}
        </div>
      )}
      {isEditing && (
        <MentionAutocomplete
          textareaRef={subtitleRef}
          docs={docs}
          refs={refs}
          onNeedReferences={onNeedReferences}
          onCreateReference={onCreateReference}
          refsLoading={refsLoading}
          onInsert={(newValue, newCaret) => {
            const toToken = (value) => value.replace(/<span[^>]*data-ref="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1');
            const value = toToken(newValue);
            const caret = toToken(newValue.slice(0, newCaret)).length;
            onFieldChange?.('subtitle', value);
            window.requestAnimationFrame(() => {
              const textarea = subtitleRef.current;
              if (textarea) { textarea.focus(); textarea.setSelectionRange(caret, caret); }
            });
          }}
        />
      )}
      {isEditing && (
        <div className="genbi-figure-edit-field mb-2">
          <span className="ev-fs-11 ev-small-p-blue ev-bold-med mb-1 d-inline-block">
            <i className="bi bi-type-h1"></i> <b>Chart Title</b>
          </span>
          <input
            type="text"
            className="formField2 ev-fs-11 w-100"
            value={plotTitleText ?? ''}
            onChange={(e) => onFieldChange?.('title', e.target.value)}
            placeholder="Edit chart title..."
          />
        </div>
      )}
      <Plot
        data={isNetworkDiagram 
          ? figureDict.data.map(trace => ({
              ...trace,
              ...(trace.mode?.includes('text') && {
                textfont: { ...trace.textfont, color: themeColors.fontColor }
              }),
              ...(trace.mode === 'lines' && {
                line: { ...trace.line, color: isDarkMode ? '#48CAE4' : '#0077B6' }
              }),
              ...(trace.marker && {
                marker: { ...trace.marker, color: isDarkMode ? '#0096C7' : '#0077B6', line: { ...trace.marker?.line, color: isDarkMode ? '#48CAE4' : '#023E8A' } }
              }),
            }))
          : figureDict.data
        }
        ref={plotRef}
        layout={{
          ...figureDict.layout,
          
          // Main chart title
          title: {
            ...plotTitleConfig,
            font: {
              ...baseTitleConfig?.font,
              family: fontFamily,
              color: themeColors.titleColor,
              size: mainTitleSize,
              weight: mainTitleWeight
            },
            y: 0.95,
            yref: 'container',
            yanchor: 'top',
            x: 0.5,
            xanchor: 'center'
          },
          
          paper_bgcolor: themeColors.paper_bgcolor,
          plot_bgcolor: themeColors.plot_bgcolor,
          font: {
            ...figureDict.layout?.font,
            family: fontFamily,
            color: themeColors.fontColor,
            size: fontSize
          },
          
          // Hover label styling
          hoverlabel: {
            ...figureDict.layout?.hoverlabel,
            bgcolor: themeColors.paper_bgcolor,
            bordercolor: themeColors.gridColor,
            font: {
              family: fontFamily,
              size: fontSize,
              color: themeColors.fontColor
            }
          },
          
          // 3D CHARTS
          scene: {
            ...figureDict.layout?.scene,
            bgcolor: themeColors.plot_bgcolor,
            xaxis: {
              ...figureDict.layout?.scene?.xaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...figureDict.layout?.scene?.xaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              },
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            yaxis: {
              ...figureDict.layout?.scene?.yaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...figureDict.layout?.scene?.yaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              },
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            zaxis: {
              ...figureDict.layout?.scene?.zaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...figureDict.layout?.scene?.zaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              },
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            }
          },

          // RADAR/POLAR CHARTS
          polar: {
            ...figureDict.layout?.polar,
            bgcolor: themeColors.plot_bgcolor,
            radialaxis: {
              ...figureDict.layout?.polar?.radialaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              },
              title: {
                ...figureDict.layout?.polar?.radialaxis?.title,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: axisSize,
                  weight: axisTitleWeight
                }
              }
            },
            angularaxis: {
              ...figureDict.layout?.polar?.angularaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            }
          },

          // GEO CHARTS
          geo: {
            ...figureDict.layout?.geo,
            bgcolor: themeColors.plot_bgcolor,
            lakecolor: themeColors.plot_bgcolor,
            landcolor: themeColors.plot_bgcolor,
            oceancolor: themeColors.plot_bgcolor,
            countrycolor: themeColors.fontColor,
            subunitcolor: themeColors.gridColor,
            coastlinecolor: themeColors.fontColor,
            framecolor: themeColors.fontColor,
            projection: {
              ...figureDict.layout?.geo?.projection
            }
          },

          // TERNARY CHARTS
          ternary: {
            ...figureDict.layout?.ternary,
            bgcolor: themeColors.plot_bgcolor,
            aaxis: {
              ...figureDict.layout?.ternary?.aaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            baxis: {
              ...figureDict.layout?.ternary?.baxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            },
            caxis: {
              ...figureDict.layout?.ternary?.caxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { 
                family: fontFamily,
                color: themeColors.fontColor, 
                size: fontSize 
              }
            }
          },
          
          autosize: true,
          margin: isNetworkDiagram 
            ? { l: 10, r: 10, t: 10, b: 10 }
            : { l: marginLeft, r: marginRight, t: 55, b: marginBottom },
          
          // Apply all axes dynamically (xaxis, yaxis, xaxis2, yaxis2, etc.)
          ...(isNetworkDiagram ? {
            xaxis: { visible: false, showgrid: false, zeroline: false, showline: false, showticklabels: false },
            yaxis: { visible: false, showgrid: false, zeroline: false, showline: false, showticklabels: false },
          } : dynamicAxes),
          
          // Apply annotations - preserve original for network diagrams  
          ...(isNetworkDiagram 
            ? { annotations: (figureDict.layout?.annotations || []).map(a => ({
                ...a,
                font: { ...a.font, color: themeColors.fontColor },
                bgcolor: isDarkMode ? 'rgba(40,42,70,0.95)' : 'rgba(255,255,255,0.9)',
                bordercolor: isDarkMode ? 'rgba(112,152,255,0.5)' : '#90E0EF',
              }))
            }
            : (dynamicAnnotations && { annotations: dynamicAnnotations })
          ),
          
          // Legend
          ...(isNetworkDiagram 
            ? { showlegend: false }
            : { legend: {
                ...figureDict.layout?.legend,
                bgcolor: themeColors.paper_bgcolor,
                bordercolor: themeColors.gridColor,
                font: { 
                  family: fontFamily,
                  color: themeColors.fontColor, 
                  size: fontSize 
                },
                // Standalone: legend to the right of plot area.
                orientation: 'v',
                x         : 1.02,
                y         : 1,
                xanchor   : 'left',
                yanchor   : 'top',
              }
            }
          ),
           
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
          doubleClick: 'reset',
          showTips: false
        }}
        className="w-100 vh-50"
        useResizeHandler={false}
        key={isDarkMode ? 'dark' : 'light'}
      />
      {footer && (
        <div className="mb-2">
          <span className="ev-message-card2 w-100 m-0 ev-fs-11 text-wrap">
            <i className="bi bi-pencil-square"></i> <b>Note </b> <span dangerouslySetInnerHTML={{ __html: processedFooter }} />
          </span>
        </div>
      )}
    </div>
  );
};

export default GraphComponent;